import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { UpsellFooter } from "./UpsellPrimitives";
import SharedInfoTooltip from "../../../components/InfoTooltip";
import useDrmMetric from "./useDrmMetrics";
import { useHistory } from 'react-router';
import useOrganisationInfo from '../../../hooks/useOrganisationInfo';
import { Modules } from '../../../util/constants';
import { pendo } from '../../../util/logging';
import useModuleInfo from "@/hooks/useModuleInfo";

const fmtCurrency = (amount, currency = "GBP") => {
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  const code = currency || "GBP";

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {

    return `${code} ${value.toLocaleString("en-GB")}`;
  }
};

// Sentence case: only the first letter is capitalised (e.g. "repeated_late" →
// "Repeated late"). Used for dashboard flag/signal headings so they read as
// sentence case rather than Title Case.
const sentenceCase = (value = "") => {
  const spaced = value.replace(/_/g, " ").trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const pluralize = (count, singular, plural = `${singular}s`) =>
  Number(count) === 1 ? singular : plural;

const normalizeDebtorTerminology = (value = '') =>
  String(value)
    .replace(/\bcustomers\b/gi, (match) => (match[0] === 'C' ? 'Debtors' : 'debtors'))
    .replace(/\bcustomer\b/gi, (match) => (match[0] === 'C' ? 'Debtor' : 'debtor'));

export const TILE_INFO = {
  onTrack: {
    body: "Cash you're on track to collect before this month ends. It looks at invoices due this month or next, and counts only those expected to be paid within this month.",
    example: "e.g. an invoice due the 24th from a customer who usually pays about 3 days late would be paid around the 27th. That's still this month, so it counts.",
    note: "“Expected to be paid” is based on each invoice's expected payment date, or how long that customer usually takes to pay. Only customers who still owe you cash overall are included. Anyone in credit is left out. Shown in your base currency.",
  },
  atRisk: {
    body: "Cash due this month that's expected to be paid after the month ends.",
    example: "e.g. an invoice due the 28th from a customer who typically takes 10 extra days would be paid early next month, so it counts.",
    note: "The prediction is based on each invoice's expected payment date, or how long that customer usually takes to pay. Only customers who still owe you cash overall are included. Shown in your base currency.",
  },
  recoverToday: {
    body: "Your oldest overdue cash: invoices more than 90 days past their due date. If nothing is that old, invoices more than 60 days overdue are shown instead.",
    note: "Counts unpaid invoices you've sent, for customers who still owe you cash overall. Your strongest candidates to chase or hand to collections. Shown in your base currency.",
  },
  cashOutstanding: {
    body: "The total cash your customers owe you right now, across every unpaid invoice you've sent, whether it's due later or already overdue.",
    note: "Only customers who still owe you cash overall are included. Anyone in credit is left out. The overdue figure counts anything already past its due date. Shown in your base currency.",
  },
  expected: {
    body: "Cash expected to be paid within the next 30 days. For each unpaid invoice, its due date plus how long that customer usually takes to pay gives an expected payment date, and those falling within the next 30 days are counted.",
    example: "e.g. an invoice due in 5 days from a customer who pays about a week late is expected in about 12 days. That's within 30 days, so it counts.",
    note: "Only customers who still owe you cash overall are included. Shown in your base currency.",
  },
  dso: {
    body: "The average number of days your customers took to pay you last month.",
    example: "e.g. 32 days means invoices took just over a month to be paid, on average.",
    note: "Measured across your whole business for the previous calendar month. Unlike the other tiles, this isn't limited to customers who currently owe you.",
  },
  totalOutstanding: {
    body: "The total cash your customers owe you, across every unpaid invoice you've sent.",
    note: "Only customers who still owe you cash overall are included. Anyone in credit is left out. Shown in your base currency.",
  },
  totalOverdue: {
    body: "Cash already past its due date, across every overdue unpaid invoice you've sent.",
    note: "Only customers who still owe you cash overall are included. Shown in your base currency.",
  },
  dueThisMonth: {
    body: "Cash on unpaid invoices with a due date in this calendar month, whether they're expected on time or late.",
    note: "Based on the due date, not when payment is expected. Only customers who still owe you cash overall are included. Shown in your base currency.",
  },
  cashBands: {
    body: "Your upcoming unpaid invoices, grouped by how soon they're due: within 7 days, 8–30 days, 31–60 days, and 61–90 days.",
    note: "Grouped by due date, not when payment is expected. Invoices already overdue, or due more than 90 days away, aren't shown here. Only customers who still owe you cash overall are included. Shown in your base currency.",
  },
  brokenPromises: {
    body: "Unpaid invoices where the date the customer was expected to pay has already passed, and the cash still hasn't arrived.",
    example: "e.g. a customer was expected to pay by the 10th; it's now the 15th and the invoice is still open, so it counts.",
    note: "Counts unpaid invoices you've sent where the expected payment date is now in the past. Only customers who still owe you cash overall are included.",
  },
  repeatedLate: {
    body: "This customer has paid you late at least twice, by 4 or more days past the due date, and still owes you cash.",
  },
  predicted: {
    body: "Cash you're on track to collect before this month ends. It looks at invoices due this month or next, and counts only those expected to be paid within this month.",
    example: "e.g. an invoice due the 24th from a customer who usually pays about 3 days late would be paid around the 27th. That's still this month, so it counts.",
    note: "“Expected to be paid” is based on each invoice's expected payment date, or how long that customer usually takes to pay. Only customers who still owe you cash overall are included. Anyone in credit is left out. Shown in your base currency.",
  },
  thisWeekAiSummary: {
    body: "A plain-English rundown of your week's cash position: what's due this week, what's overdue, any open disputes and broken promises, and a few suggested actions for the team.",
    note: "Built from this week's figures (Monday to Sunday): cash due this week, overdue cash, open disputes, broken promises, DSO, and your highest-risk customers. Written up automatically; if that can't be produced, a standard summary of the same figures is shown instead. Only customers who still owe you cash overall are included.",
  },
};

export const TileInfo = ({ info, position }) => {
  if (!info) return null;
  return (
    <span className="drm-tile-info" onClick={(e) => e.stopPropagation()}>
      <SharedInfoTooltip iconSize={14} position={position}>
        <span className="drm-tile-info--body">{normalizeDebtorTerminology(info.body)}</span>
        {info.example && <span className="drm-tile-info--example">{normalizeDebtorTerminology(info.example)}</span>}
        {info.note && <span className="drm-tile-info--note">{normalizeDebtorTerminology(info.note)}</span>}
      </SharedInfoTooltip>
    </span>
  );
};

const goToOutstandingCustomers = (history) => {
  history.push({
    pathname: '/receivables/customers',
    state: {
      table: {
        filters: [{
          type: 'netBalanceAboveZero',
          label: 'Net balance above zero',
          data: undefined,
          id: 'drm-net-balance-above-zero',
        }],
        sortProp: 'Balances.AccountsReceivable.Outstanding',
        ordering: 'DESC',
      },
    },
  });
};

const isFutureDate = (value) => {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  return !Number.isNaN(timestamp) && timestamp > Date.now();
};

function useForecastCtaTarget() {
  const { cashFlowIntelligence } = useOrganisationInfo();
  const { module, loading } = useModuleInfo(Modules.CASHFLOW, {
    suspense: false,
  });
  const isCashflowSubscribed = module.isSubscribed;

  if (loading) return null;

  const hasCashflow =
    isCashflowSubscribed ||
    isFutureDate(cashFlowIntelligence?.cashFlowForecastTrialEndDate);

  if (hasCashflow) {
    return {
      label: "Cash Flow Intelligence",
      path: cashFlowIntelligence?.onboardingProcessCompleted
        ? "/cashflow/forecast/table"
        : "/cashflow/forecast/onboarding",
    };
  }

  return { label: "AR Forecast", path: "/cashflow/receivables/forecast" };
}

const ModuleState = ({ loading, message }) => (
  <div className={`drm-mod-state ${loading ? "drm-mod-state--loading" : "drm-mod-state--error"}`}>
    {message}
  </div>
);

const signalSeverity = (type) => {
  if (["amount_at_risk", "top_debtor"].includes(type)) return "high";
  if (["collection_velocity", "delaying_45_plus"].includes(type)) return "med";
  return "low";
};

const toMonthIndex = (monthKey = "") => {
  const [year, month] = monthKey.replace("_", "-").split("-");
  const y = Number(year);
  const m = Number(month);

  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  return y * 12 + (m - 1);
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return "";
  const [year, month] = monthKey.replace("_", "-").split("-");
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

const resolveDsoDisplay = (dsoData) => {
  const targetMonth = dsoData?.month || "";

  if (dsoData?.days != null) {
    return {
      days: dsoData.days,
      invoicesConsidered: dsoData?.invoicesConsidered ?? 0,
      month: targetMonth,
      isFallback: false,
      missingMonth: null,
    };
  }

  const targetMonthIndex = toMonthIndex(targetMonth);
  const historyEntries = Object.entries(dsoData?.history || {})
    .map(([key, value]) => ({
      month: key.replace("_", "-"),
      monthIndex: toMonthIndex(key),
      days: value?.daySalesOutstandingAvg,
      invoicesConsidered: value?.numberOfInvoicesConsidered ?? 0,
    }))
    .filter((entry) => (
      entry.monthIndex != null
      && entry.days != null
      && (targetMonthIndex == null || entry.monthIndex < targetMonthIndex)
    ))
    .sort((a, b) => b.monthIndex - a.monthIndex);

  const latest = historyEntries[0];
  if (!latest) {
    return {
      days: null,
      invoicesConsidered: 0,
      month: targetMonth,
      isFallback: false,
      missingMonth: null,
    };
  }

  return {
    days: latest.days,
    invoicesConsidered: latest.invoicesConsidered,
    month: latest.month,
    isFallback: true,
    missingMonth: targetMonth || null,
  };
};

/* ══════════════════════════════════════════════════════════════════════
   TIER 1 — VISIBILITY
   ══════════════════════════════════════════════════════════════════════ */

export const ExposureSummary = ({ density }) => {
  const history = useHistory();
  const { BaseCurrency } = useOrganisationInfo();
  const { data, loading } = useDrmMetric("exposureSummary");

  if (loading) {
    return <ModuleState loading message="Loading exposure summary…" />;
  }

  const currency = data?.currency || BaseCurrency || "GBP";
  const stats = [
    {
      label: "Total Outstanding",
      short: "Outstanding",
      info: TILE_INFO.totalOutstanding,
      value: fmtCurrency(data?.totalOutstanding ?? 0, currency),
      color: "#6366f1",
      event: "dashboard_exposure_total_outstanding_clicked",
    },
    {
      label: "Total Overdue",
      short: "Overdue",
      info: TILE_INFO.totalOverdue,
      value: fmtCurrency(data?.totalOverdue ?? 0, currency),
      color: "#ef4444",
      event: "dashboard_exposure_total_overdue_clicked",
    },
    {
      label: "Due This Month",
      short: "Due",
      info: TILE_INFO.dueThisMonth,
      value: fmtCurrency(data?.dueThisMonth ?? 0, currency),
      color: "#f59e0b",
      event: "dashboard_exposure_due_this_month_clicked",
    },
    {
      label: "Predicted",
      short: "Predicted",
      info: TILE_INFO.predicted,
      value: fmtCurrency(data?.predicted ?? 0, currency),
      color: "#22c55e",
      event: "dashboard_exposure_predicted_clicked",
    },
  ];
  return (
    <div className="drm-mod-content">
      <div className="drm-mod-stats-grid" data-density={density}>
        {stats.map((s) => (
          <div
            key={s.label}
            className="drm-mod-stat-card"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => {
              pendo.track(s.event);
              goToOutstandingCustomers(history);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                pendo.track(s.event);
                goToOutstandingCustomers(history);
              }
            }}
          >
            <div className="drm-mod-stat-accent" style={{ background: s.color }} />
            <div className="drm-mod-stat-label">{density === "xs" ? s.short : s.label}<TileInfo info={s.info} /></div>
            <div className="drm-mod-stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CashOutstandingKPI = ({ density }) => {
  const history = useHistory();
  const { data, loading } = useDrmMetric("cashOutstanding");
  const { BaseCurrency } = useOrganisationInfo();

  if (loading) {
    return <ModuleState loading message="Loading cash outstanding…" />;
  }

  const currency = data?.currency || BaseCurrency || "GBP";
  const amount = data?.amount ?? 0;
  const invoiceCount = data?.invoiceCount ?? 0;
  const overdueAmount = data?.overdueAmount ?? 0;
  const percentOverdue = data?.percentOverdue ?? 0;

  return (
    <div className="drm-mod-content drm-mod-kpi drm-mod-kpi--outstanding">
      <div className="drm-mod-kpi-label">Cash Outstanding<TileInfo info={TILE_INFO.cashOutstanding} /></div>
      <div className="drm-mod-kpi-value">{fmtCurrency(amount, currency)}</div>
      {density !== "xs" && (
        <div className="drm-mod-kpi-sub">
          {density === "sm" ? (
            <>{invoiceCount} invoices · <strong>{percentOverdue}%</strong> overdue</>
          ) : (
            <>
              {invoiceCount} invoices outstanding · <strong>{fmtCurrency(overdueAmount, currency)}</strong> ({percentOverdue}%) overdue
            </>
          )}
        </div>
      )}
      <button
        type="button"
        className="drm-mod-kpi-cta"
        onClick={() => {
          pendo.track('dashboard_cash_outstanding_link_clicked');
          goToOutstandingCustomers(history);
        }}
      >
        View receivables <span aria-hidden="true">→</span>
      </button>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   CASH TIMING BANDS
   ══════════════════════════════════════════════════════════════════════ */

export const FrictionFlags = ({ density }) => {
  const history = useHistory();
  const { BaseCurrency } = useOrganisationInfo();
  const { data, loading } = useDrmMetric("paymentFriction");

  if (loading) {
    return <ModuleState loading message="Loading payment friction…" />;
  }

  const currency = data?.currency || BaseCurrency || "GBP";
  const flags = data?.items?.length
    ? data.items.map((item) => ({
        type: sentenceCase(item.type),
        rawType: item.type,
        debtor: item.contactName || "Unknown",
        amount: item.amount || 0,
        advice: item.advice || null,
        action: item.action || null,
        severity:
          item.type === "dispute" || item.type === "broken_promise"
            ? "high"
            : item.type === "repeated_late"
              ? "med"
              : "low",
      }))
    : [];

  const goToFrictionTarget = (flag) => {
    switch (flag.rawType) {
      case "dispute":
        pendo.track("dashboard_friction_dispute_clicked", {
          company_name: flag.debtor,
          amount: flag.amount,
        });
        break;
      case "repeated_late":
        pendo.track("dashboard_friction_repeated_late_clicked", {
          company_name: flag.debtor,
        });
        break;
      case "broken_promise":
        pendo.track("dashboard_friction_broken_promise_clicked", {
          company_name: flag.debtor,
          amount: flag.amount,
        });
        break;
      default:
        pendo.track("dashboard_friction_at_risk_fallback_clicked");
    }

    const contactId = flag?.action?.contactId;
    const invoiceId = flag?.action?.invoiceId;

    if (invoiceId) {
      history.push(`/receivables/invoice/${invoiceId}`);
      return;
    }

    if (contactId) {
      history.push(`/receivables/customer/${contactId}`);
      return;
    }

    history.push("/receivables/customers");
  };

  if (density === "xs") {
    const high = flags.filter(f => f.severity === "high").length;
    return (
      <div className="drm-mod-content drm-mod-summary">
        <div className="drm-mod-summary-num drm-mod-summary-num--bad">{flags.length}</div>
        <div className="drm-mod-summary-label">friction flags<br/><strong>{high}</strong> high severity</div>
        <SharedInfoTooltip iconSize={14}>
          <strong>Friction detail</strong>
          {flags.map((f, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>{f.type} · {f.debtor}</span><strong>{fmtCurrency(f.amount, currency)}</strong>
            </div>
          ))}
        </SharedInfoTooltip>
        <UpsellFooter
          target="receivables"
          text="Resolve in"
          onLinkClick={() => pendo.track('dashboard_friction_resolve_receivables_clicked')}
        />
      </div>
    );
  }
  return (
    <div className="drm-mod-content">
      <div className="drm-mod-friction-list">
        {flags.map((f, i) => (
          <div
            key={i}
            className={`drm-mod-friction-row drm-mod-friction-row--${f.severity}`}
            role="button"
            tabIndex={0}
            onClick={() => goToFrictionTarget(f)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                goToFrictionTarget(f);
              }
            }}
          >
            <div className="drm-mod-friction-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/>
              </svg>
            </div>
            <div className="drm-mod-friction-body">
              <div className="drm-mod-friction-type">
                {f.type}
                {f.rawType === "repeated_late" && f.advice && (
                  // The whole row navigates on click - stop it here so clicking
                  // near the icon doesn't fire that instead of just hovering.
                  <span onClick={(e) => e.stopPropagation()}>
                    <SharedInfoTooltip
                      iconSize={14}
                      onToggle={() => pendo.track('dashboard_friction_info_tooltip_hovered')}
                    >
                      <strong>Repeated late</strong>
                      <span className="drm-tile-info--body">{TILE_INFO.repeatedLate.body}</span>
                      <span className="drm-tile-info--note">{f.advice}</span>
                    </SharedInfoTooltip>
                  </span>
                )}
                {f.rawType === "broken_promise" && (
                  // Direct SharedInfoTooltip, not TileInfo - this row needs dynamic
                  // heading/body markup TileInfo doesn't support, but should still
                  // match the same 14px icon size used everywhere else.
                  <span onClick={(e) => e.stopPropagation()}>
                    <SharedInfoTooltip iconSize={14}>
                      <strong>Broken promise</strong>
                      <span className="drm-tile-info--body">{TILE_INFO.brokenPromises.body}</span>
                      <span className="drm-tile-info--example">{TILE_INFO.brokenPromises.example}</span>
                      <span className="drm-tile-info--note">{TILE_INFO.brokenPromises.note}</span>
                    </SharedInfoTooltip>
                  </span>
                )}
              </div>
                {density === "md" && <div className="drm-mod-friction-debtor">{f.debtor} · {fmtCurrency(f.amount, currency)}</div>}
              {density === "sm" && <div className="drm-mod-friction-debtor">{f.debtor}</div>}
            </div>
          </div>
        ))}
      </div>
      <UpsellFooter
        target="receivables"
        text="Resolve disputes and broken promises in"
        onLinkClick={() => pendo.track('dashboard_friction_resolve_receivables_clicked')}
      />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   CASH TIMING BANDS + ACCELERATION
   ══════════════════════════════════════════════════════════════════════ */

const TIMING_BAND_EVENTS_BY_INDEX = [
  "dashboard_timing_band_7d_clicked",
  "dashboard_timing_band_8_30d_clicked",
  "dashboard_timing_band_31_60d_clicked",
  "dashboard_timing_band_61_90d_clicked",
];

export const CashTimingBands = ({ density }) => {
  const { BaseCurrency } = useOrganisationInfo();
  const { data, loading } = useDrmMetric("cashTimingBands");
  const forecastCta = useForecastCtaTarget();

  if (loading) {
    return <ModuleState loading message="Loading cash timing…" />;
  }

  const confForLabel = (label = "") => {
    if (label.toLowerCase().includes("overdue")) return "high";
    if (label.toLowerCase().includes("0-7") || label.toLowerCase().includes("8-30")) return "med";
    return "low";
  };

  const rawBands = data?.bands?.length
    ? data.bands.filter((b) => !b.label.toLowerCase().includes("overdue"))
    : [
        { label: "Next 7 days", amount: 0 },
        { label: "8–30 days", amount: 0 },
        { label: "31–60 days", amount: 0 },
        { label: "61–90 days", amount: 0 },
      ];

  const bands = rawBands.map((b) => ({
    ...b,
    conf: b.conf || confForLabel(b.label),
  }));

  const currency = data?.currency || BaseCurrency || "GBP";
  const max = Math.max(...bands.map((b) => b.amount));
  const total = bands.reduce((s, b) => s + b.amount, 0);
  if (density === "xs") {
    return (
      <div className="drm-mod-content">
        <div className="drm-mod-bands-stacked">
          {bands.map((b, i) => (
            <div
              key={b.label}
              className={`drm-mod-band-seg drm-mod-band-seg--${b.conf}`}
              style={{ width: `${total > 0 ? (b.amount / total) * 100 : 0}%`, cursor: TIMING_BAND_EVENTS_BY_INDEX[i] ? "pointer" : undefined }}
              title={`${b.label}: ${fmtCurrency(b.amount, currency)}`}
              onClick={() => {
                const event = TIMING_BAND_EVENTS_BY_INDEX[i];
                if (event) pendo.track(event);
              }}
            />
          ))}
        </div>
        <div className="drm-mod-bands-total">
          <span>Total incoming</span>
          <strong>{fmtCurrency(total, currency)}</strong>
          <SharedInfoTooltip iconSize={14}>
            <strong>Cash timing</strong>
            {bands.map((b) => (
              <div key={b.label} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span>{b.label}</span><strong>{fmtCurrency(b.amount, currency)}</strong>
              </div>
            ))}
          </SharedInfoTooltip>
        </div>
        {forecastCta && (
          <UpsellFooter
            target="cashflow"
            text="Forecast in"
            label={forecastCta.label}
            path={forecastCta.path}
            onLinkClick={() => pendo.track('dashboard_timing_band_cfi_link_clicked')}
          />
        )}
      </div>
    );
  }
  return (
    <div className="drm-mod-content">
      <div className="drm-mod-bands-list">
        {bands.map((b, i) => (
          <div
            key={b.label}
            className="drm-mod-band-row"
            style={{ cursor: TIMING_BAND_EVENTS_BY_INDEX[i] ? "pointer" : undefined }}
            onClick={() => {
              const event = TIMING_BAND_EVENTS_BY_INDEX[i];
              if (event) pendo.track(event);
            }}
          >
            <div className="drm-mod-band-label">{b.label}</div>
            <div className="drm-mod-band-track">
              <div className={`drm-mod-band-fill drm-mod-band-fill--${b.conf}`} style={{ width: `${max > 0 ? (b.amount / max) * 100 : 0}%` }} />
            </div>
            {density !== "sm" && <div className="drm-mod-band-amount">{fmtCurrency(b.amount, currency)}</div>}
          </div>
        ))}
      </div>
      {forecastCta && (
        <UpsellFooter
          target="cashflow"
          text="See full forecast & scenarios in"
          label={forecastCta.label}
          path={forecastCta.path}
          onLinkClick={() => pendo.track('dashboard_timing_band_cfi_link_clicked')}
        />
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   THIS WEEK + IMPACT SIGNALS
   ══════════════════════════════════════════════════════════════════════ */

/* -- Expected (Upcoming Cash Inflows) KPI -- */
export const DueSoonKPI = ({ density }) => {
  const history = useHistory();
  const { data, loading } = useDrmMetric("expected");
  const { data: dsoData } = useDrmMetric("dso");
  const { BaseCurrency } = useOrganisationInfo();

  if (loading) {
    return <ModuleState loading message="Loading expected cash…" />;
  }

  const currency = data?.currency || BaseCurrency || "GBP";
  const amount = data?.amount ?? 0;
  const count = data?.count ?? 0;
  const dsoDisplay = resolveDsoDisplay(dsoData);
  const dsoDays = dsoDisplay.days;

  return (
    <div className="drm-mod-content drm-mod-kpi drm-mod-kpi--inflows">
      <div className="drm-mod-kpi-label">Expected<TileInfo info={TILE_INFO.expected} /></div>
      <div className="drm-mod-kpi-value">{fmtCurrency(amount, currency)}</div>
      {density !== "xs" && (
        <div className="drm-mod-kpi-sub">
          {count} invoices in next 30 days
          {dsoDays != null && (
            <>
              {" "}· DSO <strong>{dsoDays} days</strong>
              {dsoDisplay.isFallback && density === "md" && " (latest available)"}
            </>
          )}
        </div>
      )}
      <button
        type="button"
        className="drm-mod-kpi-cta"
        onClick={() => {
          pendo.track('dashboard_expected_forecast_link_clicked');
          history.push("/cashflow/receivables/forecast");
        }}
      >
        View forecast <span aria-hidden="true">→</span>
      </button>
    </div>
  );
};

/* -- DSO (Days Sales Outstanding) KPI -- */
export const DsoKPI = ({ density }) => {
  const { data, loading } = useDrmMetric("dso");

  if (loading) {
    return <ModuleState loading message="Loading DSO…" />;
  }

  const dsoDisplay = resolveDsoDisplay(data);
  const days = dsoDisplay.days;
  const invoicesConsidered = dsoDisplay.invoicesConsidered;
  const month = dsoDisplay.month;

  return (
    <div
      className="drm-mod-content drm-mod-kpi drm-mod-kpi--dso"
      role="button"
      tabIndex={0}
      style={{ cursor: "pointer" }}
      onClick={() => pendo.track('dashboard_dso_tile_clicked')}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          pendo.track('dashboard_dso_tile_clicked');
        }
      }}
    >
      <div className="drm-mod-kpi-label">Days Sales Outstanding<TileInfo info={TILE_INFO.dso} /></div>
      <div className="drm-mod-kpi-value">{days != null ? `${days} days` : "—"}</div>
      {density !== "xs" && (
        <div className="drm-mod-kpi-sub">
          {days != null
            ? (
              dsoDisplay.isFallback
                ? (
                  <>
                    Showing latest available DSO for <strong>{formatMonthLabel(month)}</strong>
                    {" "}based on <strong>{invoicesConsidered}</strong> invoices
                    {dsoDisplay.missingMonth && (
                      <>
                        {" "}({formatMonthLabel(dsoDisplay.missingMonth)} is not calculated yet)
                      </>
                    )}
                  </>
                )
                : <>Previous month's DSO based on <strong>{invoicesConsidered}</strong> invoices</>
            )
            : "No DSO data available yet"}
          {density === "md" && month && !dsoDisplay.isFallback && <> · {formatMonthLabel(month)}</>}
        </div>
      )}
    </div>
  );
};

/* -- Dynamic Insight Line (3 stat-driven sentences) -- */
export const DynamicInsightLine = ({ density }) => {
  const history = useHistory();
  const { data: onTrack, loading: onTrackLoading } = useDrmMetric("onTrack");
  const { data: atRisk, loading: atRiskLoading } = useDrmMetric("atRisk");
  const { data: recoverToday, loading: recoverLoading } = useDrmMetric("recoverToday");
  const { BaseCurrency } = useOrganisationInfo();

  if (onTrackLoading && atRiskLoading && recoverLoading) {
    return <ModuleState loading message="Loading today’s cash position…" />;
  }

  const onTrackAmount = onTrack?.amount ?? 0;
  const atRiskAmount = atRisk?.amount ?? 0;
  const recoverAmount = recoverToday === null ? 0 : (recoverToday?.amount ?? 0);
  const recoverActions = recoverToday === null ? 0 : (recoverToday?.actionCount ?? 0);
  const onTrackCurrency = onTrack?.currency || BaseCurrency || "GBP";
  const atRiskCurrency = atRisk?.currency || BaseCurrency || "GBP";
  const recoverCurrency = recoverToday?.currency || BaseCurrency || "GBP";

  const lines = [
    {
      tone: "positive",
      icon: "target",
      eyebrow: "On track",
      info: TILE_INFO.onTrack,
      value: fmtCurrency(onTrackAmount, onTrackCurrency),
      tail: "to collect this month",
      cta: "View customers and invoices",
      compact: <>On track: <strong>{fmtCurrency(onTrackAmount, onTrackCurrency)}</strong> this month</>,
      event: "dashboard_on_track_cta_clicked",
      onNavigate: goToOutstandingCustomers,
    },
    {
      tone: "warning",
      icon: "alert",
      eyebrow: "At risk",
      info: TILE_INFO.atRisk,
      value: fmtCurrency(atRiskAmount, atRiskCurrency),
      tail: "mainly from late payments",
      cta: "Review at risk",
      compact: <><strong>{fmtCurrency(atRiskAmount, atRiskCurrency)}</strong> at risk</>,
      event: "dashboard_at_risk_cta_clicked",
      path: "/cashflow/receivables/forecast",
    },
    {
      tone: "action",
      icon: "spark",
      eyebrow: "Recover today",
      info: TILE_INFO.recoverToday,
      value: fmtCurrency(recoverAmount, recoverCurrency),
      tail: <>by taking <strong>{recoverActions} {pluralize(recoverActions, 'action')}</strong></>,
      cta: "Take action",
      compact: <>Recover <strong>{fmtCurrency(recoverAmount, recoverCurrency)}</strong> today</>,
      event: "dashboard_recover_today_cta_clicked",
      path: "receivables/collections",
    },
  ];
  const iconFor = (name) => {
    const props = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (name) {
      case "target": return <svg {...props}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
      case "alert":  return <svg {...props}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>;
      case "spark":  return <svg {...props}><path d="M12 2v6"/><path d="M12 16v6"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="M2 12h6"/><path d="M16 12h6"/><path d="m4.93 19.07 4.24-4.24"/><path d="m14.83 9.17 4.24-4.24"/></svg>;
      default: return null;
    }
  };
  return (
    <div className="drm-mod-content drm-mod-dynamic-insight">
      <div className="drm-mod-dyn-header">
        <span className="drm-mod-dyn-spark" aria-hidden="true">✦</span>
        <span>Your cash position today</span>
        <span className="drm-mod-dyn-pulse" aria-hidden="true" />
        <span className="drm-mod-dyn-live">Live</span>
      </div>
      <ul className="drm-mod-dyn-list">
        {lines.map((l, i) => (
          <li key={i} className={`drm-mod-dyn-line drm-mod-dyn-line--${l.tone}`}>
            {density === "xs" ? (
              <>
                <span className="drm-mod-dyn-icon">{iconFor(l.icon)}</span>
                <span className="drm-mod-dyn-text">{l.compact}</span>
              </>
            ) : (
              <>
                <div className="drm-mod-dyn-line-top">
                  <span className="drm-mod-dyn-icon">{iconFor(l.icon)}</span>
                  <span className="drm-mod-dyn-eyebrow">{l.eyebrow}</span>
                  <TileInfo info={l.info} />
                </div>
                <div className="drm-mod-dyn-value">{l.value}</div>
                <div className="drm-mod-dyn-tail">{l.tail}</div>
                {l.cta && (
                  <button
                    type="button"
                    className="drm-mod-dyn-line-cta"
                    onClick={() => {
                      if (l.event) pendo.track(l.event);
                      return l.onNavigate ? l.onNavigate(history) : history.push(l.path);
                    }}
                  >
                    {l.cta} <span aria-hidden="true">→</span>
                  </button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {density === "md" && (
        <button type="button" className="drm-mod-dyn-cta" onClick={(e) => e.preventDefault()}>
          See what to do <span aria-hidden="true">→</span>
        </button>
      )}
    </div>
  );
};

/* -- Cash Impact Signals (narrative bullet list) -- */
export const CashImpactSignals = ({ density }) => {
  const history = useHistory();
  const { BaseCurrency } = useOrganisationInfo();
  const { data, loading } = useDrmMetric("cashImpactSignals");

  if (loading) {
    return <ModuleState loading message="Loading cash impact signals…" />;
  }

  const currency = data?.currency || BaseCurrency || "GBP";
  const liveSignals = (data?.signals || []).map((signal) => {
    switch (signal.type) {
      case "amount_at_risk":
        return {
          icon: "money",
          severity: "high",
          action: signal.action || null,
          text: <><strong>{fmtCurrency(signal.value || 0, currency)}</strong> at risk if current trends continue</>,
          type: signal.type,
          amountAtRisk: signal.value || 0,
        };

      case "delaying_45_plus":
        return {
          icon: "users",
          severity: "med",
          action: signal.action || null,
          text: <><strong>{signal.count || 0} {pluralize(signal.count || 0, 'debtor')}</strong> delaying 45+ days · <strong>{fmtCurrency(signal.value || 0, currency)}</strong></>,
          type: signal.type,
          customerCount: signal.count || 0,
        };

      case "collection_velocity": {
        const velocityText = signal.trend || "steady";
        return {
          icon: "trend",
          severity: velocityText === "slowing" ? "high" : "low",
          action: signal.action || null,
          text: <>
            Collections velocity {velocityText}
            {signal.value != null ? <> · DSO <strong>{signal.value} days</strong></> : null}
          </>,
          type: signal.type,
          trend: velocityText,
        };
      }

      case "top_debtor":
        if (signal.variant === "disputed") {
          return {
            icon: "alert",
            severity: "high",
            action: signal.action || null,
            text: <>
              Top disputed invoice: <strong>{signal.label || "N/A"}</strong>
              {signal.disputeAgeDays != null ? <> · <strong>{signal.disputeAgeDays} days</strong> open</> : null}
              {" "}· <strong>{fmtCurrency(signal.value || 0, currency)}</strong>
            </>,
            type: signal.type,
            variant: signal.variant,
            daysSinceRaised: signal.disputeAgeDays,
          };
        }

        return {
          icon: "alert",
          severity: "med",
          action: signal.action || null,
          text: <>
            Top debtor outstanding: <strong>{signal.label || "N/A"}</strong> · <strong>{fmtCurrency(signal.value || 0, currency)}</strong>
          </>,
          type: signal.type,
          variant: signal.variant || "outstanding",
          netBalance: signal.value || 0,
        };

      default:
        return {
          icon: "alert",
          severity: signalSeverity(signal.type),
          action: signal.action || null,
          text: <>{signal.label || sentenceCase(signal.type)}</>,
          type: signal.type,
        };
    }
  });

  const signals = liveSignals.length
    ? liveSignals
    : [
        { icon: "money", severity: "high", text: <><strong>{fmtCurrency(0, currency)}</strong> at risk if current trends continue</> },
        { icon: "users", severity: "med", text: <><strong>0 debtors</strong> delaying · avg 0 days late</> },
        { icon: "trend", severity: "low", text: <>Collections velocity slowing</> },
      ];

  const iconFor = (name) => {
    const stroke = { width: 14, height: 14, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (name) {
      case "money":    return <svg {...stroke}><path d="M12 2v20"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
      case "calendar": return <svg {...stroke}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>;
      case "users":    return <svg {...stroke}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>;
      case "alert":    return <svg {...stroke}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>;
      case "trend":    return <svg {...stroke}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>;
      default: return null;
    }
  };
  const visible = density === "xs" ? signals.slice(0, 2) : density === "sm" ? signals.slice(0, 3) : signals;
  return (
    <div className="drm-mod-content drm-mod-signals">
      <div className="drm-mod-signals-header">What can impact your cash</div>
      <ul className="drm-mod-signals-list">
        {visible.map((s, i) => {
          const isClickable = Boolean(
            s?.action?.invoiceId || s?.action?.contactIds?.length || s?.action?.contactId || s?.action?.type,
          );
          const trackSignalClick = (signal) => {
            switch (signal.type) {
              case "amount_at_risk":
                pendo.track("dashboard_impact_at_risk_trends_clicked", {
                  amount_at_risk: signal.amountAtRisk,
                });
                break;
              case "delaying_45_plus":
                pendo.track("dashboard_impact_delaying_customers_clicked", {
                  customer_count: signal.customerCount,
                });
                break;
              case "collection_velocity":
                if (signal.trend === "slowing") {
                  pendo.track("dashboard_impact_collection_velocity_clicked", {
                    trend: signal.trend,
                  });
                } else if (signal.trend === "increasing") {
                  pendo.track("dashboard_impact_velocity_increasing_clicked", {
                    trend: signal.trend,
                  });
                }
                break;
              case "top_debtor":
                if (signal.variant === "disputed") {
                  pendo.track("dashboard_impact_top_debtor_disputed_clicked", {
                    days_since_raised: signal.daysSinceRaised,
                  });
                } else {
                  pendo.track("dashboard_impact_top_debtor_outstanding_clicked", {
                    net_balance: signal.netBalance,
                  });
                }
                break;
              default:
                break;
            }
          };

          const goToSignalTarget = () => {
            trackSignalClick(s);

            if (s?.action?.invoiceId) {
              history.push(`/receivables/invoice/${s.action.invoiceId}`);
              return;
            }

            if (s?.action?.contactIds?.length) {
              history.push({
                pathname: '/receivables/customers',
                state: {
                  table: {
                    filters: [{
                      type: 'contactIds',
                      label: `${s.action.contactIds.length} debtors`,
                      data: s.action.contactIds,
                      id: `drm-signal-${s.icon}`,
                    }],
                  },
                },
              });
              return;
            }

            if (s?.action?.contactId) {
              history.push(`/receivables/customer/${s.action.contactId}`);
              return;
            }

            if (s?.action?.type) {
              history.push("/receivables/customers");
            }
          };

          return (
          <li
            key={i}
            className={`drm-mod-signal drm-mod-signal--${s.severity} ${isClickable ? "is-clickable" : ""}`}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={isClickable ? goToSignalTarget : undefined}
            onKeyDown={isClickable ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                goToSignalTarget();
              }
            } : undefined}
          >
            <span className="drm-mod-signal-icon">{iconFor(s.icon)}</span>
            <span className="drm-mod-signal-text">{s.text}</span>
          </li>
          );
        })}
      </ul>
    </div>
  );
};

function WeeklyAiDetails({ aiSummaryData }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const closeTimer = useRef(null);

  const keyFindings = aiSummaryData?.keyFindings || [];
  const recommendations = aiSummaryData?.recommendations || [];
  const trendAnalysis = aiSummaryData?.trendAnalysis;
  const hasDetails = keyFindings.length > 0 || recommendations.length > 0 || Boolean(trendAnalysis);

  const updatePosition = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;

    const POPOVER_WIDTH = 340;
    const GAP = 10;
    const VIEWPORT_TOP_OFFSET = 16;

    let left = r.left - POPOVER_WIDTH - GAP;
    if (left < 8) left = r.right + GAP;

    setPos({
      top: VIEWPORT_TOP_OFFSET,
      left: Math.max(8, Math.min(left, window.innerWidth - POPOVER_WIDTH - 8)),
    });
  };

  const show = () => {
    clearTimeout(closeTimer.current);
    updatePosition();
    setOpen(true);
  };

  const scheduleHide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (!hasDetails) return null;

  return (
    <div className="drm-mod-story-details">
      <button
        ref={triggerRef}
        type="button"
        className="drm-mod-story-details--trigger"
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
      >
        View more detail
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="drm-mod-story-details--chevron">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && typeof document !== "undefined" && ReactDOM.createPortal(
        <div
          className="drm-mod-story-details--popover"
          role="tooltip"
          style={{ position: "fixed", top: pos.top, left: pos.left }}
          onMouseEnter={show}
          onMouseLeave={scheduleHide}
        >
          <div className="drm-mod-story-details--header">
            <span className="drm-mod-story-details--header-icon" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l1.8 5.6L19 9l-5.2 1.4L12 16l-1.8-5.6L5 9l5.2-1.4z" />
              </svg>
            </span>
            AI Summary
          </div>
          {keyFindings.length > 0 && (
            <div className="drm-mod-story-details--section">
              <div className="drm-mod-story-details--label">Key Findings</div>
              <ul>
                {keyFindings.map((finding, i) => <li key={i}>{finding}</li>)}
              </ul>
            </div>
          )}
          {trendAnalysis && (
            <div className="drm-mod-story-details--section">
              <div className="drm-mod-story-details--label">Trend Analysis</div>
              <p>{trendAnalysis}</p>
            </div>
          )}
          {recommendations.length > 0 && (
            <div className="drm-mod-story-details--section">
              <div className="drm-mod-story-details--label">Recommendations</div>
              <ul>
                {recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
              </ul>
            </div>
          )}
          <div className="drm-mod-story-details--footer">
            AI-generated content may be inaccurate.{' '}
            <a href="https://www.chaserhq.com/terms" target="_blank" rel="noopener noreferrer">
              See terms
            </a>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/* -- This Week's Story (AI prose briefing) -- */
export const WeeklyStory = ({ density }) => {
  const { BaseCurrency } = useOrganisationInfo();
  const { data, loading } = useDrmMetric("thisWeek");
  const { data: aiSummaryData } = useDrmMetric("thisWeekAiSummary");
  const [aiVisible, setAiVisible] = useState(false);
  const storyRef = useRef(null);
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!aiSummaryData?.summary) {
      setAiVisible(false);
      return undefined;
    }
    // Fade the AI prose in on the next tick rather than the same render, so the
    // opacity transition in DRMDashboard.scss actually animates instead of snapping.
    const timer = setTimeout(() => setAiVisible(true), 50);
    return () => clearTimeout(timer);
  }, [aiSummaryData]);

  useEffect(() => {
    if (hasTrackedView.current || typeof IntersectionObserver === "undefined") return undefined;
    const node = storyRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !hasTrackedView.current) {
        hasTrackedView.current = true;
        pendo.track('dashboard_this_week_summary_viewed');
        observer.disconnect();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading]);

  if (loading) {
    return <ModuleState loading message="Loading this week summary…" />;
  }

  const currency = data?.currency || BaseCurrency || "GBP";
  const dueAmount = data?.dueThisWeek?.amount ?? 0;
  const dueCount = data?.dueThisWeek?.count ?? 0;
  const overdueAmount = data?.totalOverdue?.amount ?? 0;
  const overdueCount = data?.totalOverdue?.count ?? 0;
  const disputes = data?.disputes?.count ?? 0;
  const brokenPromises = data?.brokenPromises?.count ?? 0;
  const topRiskName = data?.topRiskContacts?.[0]?.name;
  // AI-generated prose is a progressive enhancement over the deterministic summary — it
  // replaces it once ready, but the widget never blocks on it and falls back silently on error.
  const summary = normalizeDebtorTerminology(aiSummaryData?.summary || data?.summary);
  const isAiSummary = Boolean(aiSummaryData?.summary);

  const formatDate = (iso) => {
    if (!iso) return null;
    const date = new Date(iso);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const weekLabel = data?.weekStart && data?.weekEnd
    ? `${formatDate(data.weekStart)} – ${formatDate(data.weekEnd)}`
    : "Apr 19 – 25";

  return (
    <div className="drm-mod-content drm-mod-story" ref={storyRef}>
      <div className="drm-mod-story-header">
        <span className="drm-mod-story-badge">This Week</span>
        <span className="drm-mod-story-date">{weekLabel}</span>
      </div>
      <p className={`drm-mod-story-prose${isAiSummary ? ` drm-mod-story-prose--ai${aiVisible ? " is-visible" : ""}` : ""}`}>
        {summary || (
          <>
            <strong>{dueCount}</strong> invoices due this week worth <strong>{fmtCurrency(dueAmount, currency)}</strong>.
            <strong> {overdueCount}</strong> remain overdue ({fmtCurrency(overdueAmount, currency)}).
            {density !== "xs" && (
              <>
                {" "}<strong>{disputes}</strong> disputes and <strong>{brokenPromises}</strong> broken promises need attention.
                {density === "md" && topRiskName && (
                  <>
                    {" "}<strong>{topRiskName}</strong> is your largest overdue concentration.
                  </>
                )}
              </>
            )}
          </>
        )}
      </p>
      <WeeklyAiDetails aiSummaryData={aiSummaryData} />
    </div>
  );
};

const CHASER_HELP_EVENTS = {
  receivables: "dashboard_chaser_recover_cash_explore_clicked",
  cashflow: "dashboard_chaser_predictability_view_clicked",
  capital: "dashboard_chaser_working_capital_options_clicked",
};

/* -- Where Chaser Can Help (consolidated upsell trio) -- */
export const WhereChaserCanHelp = ({ density }) => {
  const history = useHistory();
  const { BaseCurrency } = useOrganisationInfo();
  const { data, loading } = useDrmMetric("cashOutstanding");
  const currency = data?.currency || BaseCurrency || "GBP";

  const options = [
    { target: "receivables", title: "Recover cash faster", sub: "Automate chase sequences",        cta: "Explore", path: "/receivables/manage/schedules" },
    { target: "cashflow",    title: "Improve predictability", sub: "Forecasts & scenarios",        cta: "View", path: "/cashflow/forecast/table" },
    { target: "capital",     title: "Unlock working capital", sub: `${fmtCurrency(data?.overdueAmount ?? 0, currency)} available today`,    cta: "Options", isComingSoon: true, path: "/capital/options" },
  ];
  const visible = density === "xs" ? options.slice(0, 1) : options;
  return (
    <div className="drm-mod-content drm-mod-where-help">
      <div className="drm-mod-where-help-header">Recommended next steps</div>
      <div className="drm-mod-where-help-list">
        {visible.map((o) => (
          <div key={o.target} className={`drm-mod-where-help-row drm-mod-where-help-row--${o.target}`}>
            <div className="drm-mod-where-help-info">
              <div className="drm-mod-where-help-title">
                {o.title}
                {o.isComingSoon && <span className="drm-mod-where-help-free">COMING SOON</span>}
              </div>
              {density !== "xs" && <div className="drm-mod-where-help-sub">{o.sub}</div>}
            </div>
            <button
              className="drm-mod-where-help-btn"
              onClick={(e) => {
                const event = CHASER_HELP_EVENTS[o.target];
                if (event) {
                  pendo.track(event, o.target === "capital" ? { amount_available: data?.overdueAmount ?? 0 } : undefined);
                }
                history.push(o.path);
              }}
            >
              {o.cta} →
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
